import Session from "./Session";

/**
 * Read IELTS plan info from the current session user.
 * @returns {{ planType: string|null, unlockedTemplates: string[], unlockedSections: string[], hasAttempts: boolean }}
 *
 * hasAttempts defaults to false here. Components must call getMockAttempts and
 * set plan.hasAttempts = true before mapping cards for free-plan users.
 */
export function getIELTSPlan() {
    const metadata = Session.user?.metadata || {};
    console.log(Session.user);
    
    const normalizeIds = (arr) =>
        (arr || []).map((item) =>
            item && typeof item === 'object' ? String(item._id || item.id || '') : String(item)
        );

    return {
        // "unlimited" | "item_unlocked" | "free" | null
        planType: metadata.ieltsPlanType || null,
        // IDs of full-test templates the user has unlocked (backend auto-populates for free plan)
        unlockedTemplates: normalizeIds(metadata.unlockedIELTSTemplate),
        // IDs of section-test templates bought via standard_sectional
        unlockedSections: normalizeIds(metadata.unlockedIELTSSections),
        // Populated asynchronously by the component
        hasAttempts: false,
    };
}

export const ALL_FOUR_SECTIONS = ["listening", "reading", "writing", "speaking"];

/** Section names as the backend spells them, and as the card UI labels them. */
export const SECTION_LABELS = ["Listening", "Reading", "Writing", "Speaking"];

/**
 * Canonical identity of a set of sections, order-independent.
 *
 * Attempts carry no templateId, so the section set is the only key that joins
 * an attempt back to the template it was started from — createMockAttempt
 * stores exactly that template's section ids. Comparing the whole set rather
 * than testing for overlap is what keeps a one-section sectional attempt from
 * also matching the full-length template, in the case where both templates
 * reference the same section document.
 *
 * @returns {string} empty string when there are no sections — never a match
 */
export function sectionSetKey(sections) {
    const ids = (sections || []).map((s) => String(s?._id || s)).filter(Boolean);

    return ids.length ? [...new Set(ids)].sort().join("|") : "";
}

/** True when a template covers all four IELTS sections (a full-length test). */
export function hasAllFourSections(template) {
    const sections = template?.sections || [];

    if (sections.length < 4) return false;

    const names = sections.map((s) =>
        (s.name || s.sectionType || s.type || "").toLowerCase()
    );

    return ALL_FOUR_SECTIONS.every((sec) => names.includes(sec));
}

/**
 * Find the finished attempt that belongs to a template, latest first.
 *
 * @param {{ sections?: Array }} template
 * @param {Array} mockAttempts  ordered newest-first by the caller
 * @returns {Object|undefined}
 */
export function findAttemptForTemplate(template, mockAttempts) {
    const templateKey = sectionSetKey(template.sections);

    if (!templateKey) return undefined;

    return (mockAttempts || []).find(
        (attempt) =>
            attempt.finishedAt && sectionSetKey(attempt.mockSections) === templateKey
    );
}

/**
 * Resolve display state for a FULL-TEST template card (Practice by Tests).
 *
 * Rules:
 *   unlimited     → always "unattempted"
 *   item_unlocked → "unattempted" if templateId ∈ unlockedTemplates, else "locked"
 *   free/basic    → if templateId ∈ unlockedTemplates:
 *                     no prior attempts → "unattempted" (can start)
 *                     has prior attempts → "free_prompt" (card visible, click shows upgrade prompt)
 *                   else → "locked"
 *   null          → "locked"
 *
 * @param {string} templateId
 * @param {{ planType: string|null, unlockedTemplates: string[], hasAttempts: boolean }} plan
 * @returns {"unattempted"|"locked"|"free_prompt"}
 */
export function getFullTestState(templateId, plan) {
    const { planType, unlockedTemplates, hasAttempts } = plan;

    if (planType === "unlimited") return "unattempted";

    if (planType === "item_unlocked") {
        return unlockedTemplates.includes(templateId) ? "unattempted" : "locked";
    }

    if (planType === "free" || planType === "basic") {
        if (unlockedTemplates.includes(templateId)) {
            return hasAttempts ? "free_prompt" : "unattempted";
        }
        return "locked";
    }

    // null / not set
    return "locked";
}

/**
 * Resolve display state for a SECTION-TEST template card (SectionTests).
 *
 * unlockedIELTSSections is the only source here — full-template unlocks do NOT
 * cascade to section tests (separate purchases via standard_sectional).
 *
 * Rules:
 *   unlimited     → always "unattempted"
 *   item_unlocked → "unattempted" if templateId ∈ unlockedSections, else "locked"
 *   free/basic    → "locked" (section tests require a paid plan)
 *   null          → "locked"
 *
 * @param {string} templateId
 * @param {{ planType: string|null, unlockedSections: string[] }} plan
 * @returns {"unattempted"|"locked"|"free_prompt"}
 */
export function getSectionTestState(templateId, plan) {
    const { planType, unlockedSections } = plan;

    if (planType === "unlimited") return "unattempted";

    if (planType === "item_unlocked") {
        return unlockedSections.includes(templateId) ? "unattempted" : "locked";
    }

    // free/basic and null — section tests require a paid plan
    return "locked";
}
